<template>
  <div class="col-toolbar">
    <!-- ★ v29.23（用户定案）：刷新/保存配置按钮移除，改为「列探测刷新」——
         基于第 1 步表单的 SQL+数据源实时探测（不读库不写库），结果按 data_index
         合并进当前配置：已有列保留全部人工配置、新列补默认行，绝不清空。 -->
    <a-button
      size="small"
      :disabled="!model.data_source_id || !model.sql"
      :loading="probing"
      @click="runProbeRefresh"
    >
      列探测刷新
    </a-button>
    <span class="meta-tip">
      基于基础配置的 SQL + 数据源实时探测列（不落库）；已有列的人工配置合并保留，SQL
      新增列自动补默认行。视图与列配置在点「确定」时同时保存。主键<strong>可多选</strong>：
      勾 1 列时表格行键就是该列，勾 0 列或多列时改用列表接口自动生成的稳定行键
      （多列时由各主键值拼接而成），新建时探测到
      <code>id</code>
      列会自动选中（可改）。
    </span>
  </div>

  <!-- ★ v29.34（用户定案）：列配置表由 a-table 换成 s-table（@surely-vue/table，
       宿主 app 的 main.ts 全局注册 `s-table`，与 tablePro / tableConfig 的
       columnsConfig.vue 同源）。换来三点 a-table 给不了的：① 固定表头 + 表体虚拟滚动
       （18 列的长清单不卡）；② 列宽只认显式 width，不再被均分挤压；
       ③ 高度由容器实测下发（colTableHeight），父级不再出纵向滚动条。
       ⚠️ 对照坑：s-table 的「表体可视高度」是 `:height`（不是 scroll.y）；
       `rangeSelection` 默认 'single'（拖选单元格），本表整行都是输入控件 → 显式关掉，
       避免与输入框内的文字选择打架。 -->
  <div
    v-if="colTableMounted"
    ref="colTableWrapRef"
    class="col-table-wrapper"
  >
    <s-table
      :columns="COL_COLUMNS"
      :data-source="colRows"
      row-key="data_index"
      size="small"
      :rowHeight="COL_ROW_HEIGHT"
      :height="colTableHeight"
      :scroll="{ x: COL_TABLE_WIDTH }"
      :pagination="false"
      :range-selection="false"
      :loading="colLoading"
    >
      <!-- ★ v29.37（用户报障「表格里的数据改完不会立马更新」）：**所有读写都必须经
           `recordIndexs` 落到原始数据源 `colRows` 上**，禁止直接用插槽给的 `record`。
           s-table 的作者约定：它为了虚拟滚动/树形/编辑态的高性能，内部对数据做了处理，
           插槽下发的 `record` 不属于响应式数据源；`recordIndexs: number[]` 才是
           「按 rowKey 反查原始数据源」的索引路径（源码里 BodyCell 收到的就是
           `getIndexsByKey(rowKey)`；其内部 EditInput 写回也是这么定位 dataSource 的）。
           非树形数据恒为 `[i]`，故一律写 `colRows[recordIndexs[0]].xxx`。

           「立马更新」这条链是怎么闭合的（改完实测确认，别想当然）：
             ① `colRows[...]` 是**响应式读取**，依赖挂在**本组件的渲染 effect** 上
                → 改 `colRows[i].xxx` 会触发本组件重渲染；
             ② 本组件重渲染 → s-table 的 `:scroll="{ x: COL_TABLE_WIDTH }"` 是**内联对象字面量**，
                每次 render 都是新对象，且它在编译产物的 dynamicProps 里 → Vue 的
                `shouldUpdateComponent` 走 `PROPS(8)` 分支**逐键比较**（runtime-core 源码），
                `nextProps.scroll !== prevProps.scroll` 恒成立 → **s-table 必然重渲染**
                → 插槽重新求值 → 单元格立即刷新。
             ⚠️ 反过来，用 `record.xxx` 时表达式里没有本组件的响应式依赖，改它不产生任何渲染
                副作用（症状：勾了「默认排序」同行不展开、`已配 N 项` 不跟手、主键复选框点了没反应）。
             ⚠️ **别把 `:scroll` 之类的对象/数组 props 提成常量或 computed**：一旦它们的引用稳定，
                上面第 ② 步就断了，s-table 会被 `shouldUpdateComponent` 跳过、整表不再刷新。

           ⚠️ 因此插槽解构里**刻意不接 `record`** —— 留一条 `record.xxx` 都会被 vue-tsc 报
           「Cannot find name 'record'」，当作编译期护栏，防止回归。
           ⚠️ 前提：`row-key` 必须唯一（这里用 `data_index`，探测侧已按它去重、后端 unique_together 兜底）；
           rowKey 一旦重复，`getIndexsByKey` 会返回第一个匹配项 → 改 A 行会改到 B 行。 -->
      <template #bodyCell="{ column, recordIndexs }">
        <template v-if="column.key === 'column_order'">
          <a-input-number
            v-model:value="colRows[recordIndexs[0]].column_order"
            size="small"
            style="width: 50px"
            :min="0"
          />
        </template>
        <template v-else-if="column.key === 'is_primary_key'">
          <a-checkbox
            :checked="!!colRows[recordIndexs[0]].is_primary_key"
            @change="onPrimaryKeyChange(colRows[recordIndexs[0]], $event)"
          />
        </template>
        <template v-else-if="column.key === 'title'">
          <a-input
            v-model:value="colRows[recordIndexs[0]].title"
            size="small"
            allow-clear
          />
        </template>
        <template v-else-if="column.key === 'query_title'">
          <a-input
            v-model:value="colRows[recordIndexs[0]].query_title"
            size="small"
            allow-clear
          />
        </template>
        <template v-else-if="column.key === 'cell_component'">
          <a-select
            v-model:value="colRows[recordIndexs[0]].cell_component"
            size="small"
            style="width: 130px"
            allow-clear
            show-search
            option-filter-prop="label"
            placeholder="默认渲染"
            :options="CELL_COMPONENT_OPTIONS"
          />
        </template>
        <template v-else-if="column.key === 'checked'">
          <a-checkbox v-model:checked="colRows[recordIndexs[0]].checked" />
        </template>
        <template v-else-if="column.key === 'resizable'">
          <a-checkbox v-model:checked="colRows[recordIndexs[0]].resizable" />
        </template>
        <template v-else-if="column.key === 'summary'">
          <a-checkbox v-model:checked="colRows[recordIndexs[0]].summary" />
        </template>
        <template v-else-if="column.key === 'is_sortable'">
          <a-checkbox v-model:checked="colRows[recordIndexs[0]].is_sortable" />
        </template>
        <!-- ★ v29.18：默认排序三配置合一单元格——开启后出现 升/降序 + 排序顺序 -->
        <template v-else-if="column.key === 'default_sort'">
          <div class="default-sort-cell">
            <a-checkbox
              v-model:checked="colRows[recordIndexs[0]].is_default_sort"
              @change="onDefaultSortToggle(colRows[recordIndexs[0]])"
            />
            <template v-if="colRows[recordIndexs[0]].is_default_sort">
              <a-select
                v-model:value="colRows[recordIndexs[0]].default_sort_type"
                size="small"
                style="width: 68px"
                :options="[
                  { value: 'asc', label: '升序' },
                  { value: 'desc', label: '降序' },
                ]"
              />
              <a-input-number
                v-model:value="colRows[recordIndexs[0]].default_sort_order"
                size="small"
                style="width: 60px"
                :min="1"
                placeholder="顺序"
              />
            </template>
          </div>
        </template>
        <template v-else-if="column.key === 'map_color'">
          <!-- ⚠️ 传**原始行**（不是插槽 record）：模态框内部直接写 `props.record.content_map`
               / `color_map`，必须落在表格认可的原始数据源上（含 `record.cell_component = 'tag'`） -->
          <a-button
            size="small"
            style="padding: 0 4px"
            @click="openMapColorModal(colRows[recordIndexs[0]])"
          >
            {{
              mapCount(colRows[recordIndexs[0]])
                ? `已配 ${mapCount(colRows[recordIndexs[0]])} 项`
                : '配置'
            }}
          </a-button>
        </template>
        <template v-else-if="column.key === 'adv_search'">
          <!-- ⚠️ 同上：模态框直接写 `props.record.advanced_search_component` -->
          <a-button
            size="small"
            style="padding: 0 4px"
            @click="openAdvSearchModal(colRows[recordIndexs[0]])"
          >
            {{ colRows[recordIndexs[0]].advanced_search_component?.type || '配置' }}
          </a-button>
        </template>
        <template v-else-if="column.key === 'is_base_query'">
          <a-checkbox v-model:checked="colRows[recordIndexs[0]].is_base_query" />
        </template>
        <template v-else-if="column.key === 'is_default_query'">
          <a-checkbox v-model:checked="colRows[recordIndexs[0]].is_default_query" />
        </template>
        <template v-else-if="column.key === 'is_group_query'">
          <a-checkbox v-model:checked="colRows[recordIndexs[0]].is_group_query" />
        </template>
        <template v-else-if="column.key === 'action'">
          <a-popconfirm
            title="确认删除该列配置？"
            @confirm="removeColumn(colRows[recordIndexs[0]])"
          >
            <a-button
              type="link"
              danger
              size="small"
              style="padding: 0"
            >
              删除
            </a-button>
          </a-popconfirm>
        </template>
      </template>
    </s-table>
  </div>

  <!-- ★ v29.16：转换/颜色 联合配置模态框 + 高级搜索组件配置模态框 -->
  <ColumnMapColorModal
    v-model:open="mapColorOpen"
    :record="mapColorRecord!"
  />
  <ColumnSearchComponentModal
    v-model:open="advSearchOpen"
    :record="advSearchRecord!"
  />
</template>

<script setup lang="ts">
import { message } from 'ant-design-vue';
import { useResizeObserver } from '@vueuse/core';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { getApiData } from 'liyu-pc-base/api/api-model';
import { useUserStore } from 'liyu-pc-base/init-plugin';
import ColumnMapColorModal from 'liyu-pc-base/components/dbView/ColumnMapColorModal.vue';
import ColumnSearchComponentModal from 'liyu-pc-base/components/dbView/ColumnSearchComponentModal.vue';
import { extractData, toList } from './helpers';
import {
  CELL_COMPONENT_OPTIONS,
  COL_COLUMNS,
  COL_ROW_HEIGHT,
  COL_TABLE_WIDTH,
  PROBE_CELL_MAP,
  PROBE_CLASS_NAME_MAP,
} from './constants';

/* ★ v29.35（用户定案）：`dbViewConfig.vue` 按步骤拆分——「列配置」步（★ v29.45 起为 Step 3，§3.1.4 字段配置子表）。
   本步持有：列行数据 / 探测 / 主键多选（v29.40） / 删除 / 默认排序开关 / 两个行内模态框，
   以及 s-table 的「容器实测高度」链路（v29.34）。

   拆分带来的三处接口（原先是同一个 setup 里的自由变量，现在必须显式打通）：
   ① **进入本步的时机**：原来是外壳 `goNext`/`onStepChange` 里读 `colRows.length` 决定要不要自动探测，
      现在下沉为 `watch(() => props.active)` —— 外壳传 `active`（`currentStep === 3`）即可，
      顺带把 v29.34 的 `colTableMounted` 门控也挂到同一个 watch 上（挂载 + 兜底测量 + 自动探测一次做完）。
   ② **提交载荷**：外壳 `handleOk` 需要 columns / deleted_data_indexes → 由 `defineExpose` 提供
      `buildColumnsPayload` / `getDeletedIndexes` / `resetDeletedIndexes`，外壳用 ref 调用。
   ③ **清预览错误**：`runProbeRefresh` 原先顺手清 `sqlError`（第 1 步可能残留的预览报错）
      → 改为 `emit('probeStart')`，由外壳清（`sqlError` 仍是外壳持有的共享状态）。

   ⚠️ 本组件是**多根（fragment）模板**：三个根节点会原样插进外壳的
   `<div class="step-pane step-pane--col">` 里，不额外产生 DOM 层 —— 这是刻意的，
   多包一层 div 就会让 `.col-table-wrapper` 的 `flex: 1` 失去参照（父级不再是那个定高 flex column），
   整条自适应高度链路会从第一环断掉。代价：外壳不能给本组件传 class / v-show（fragment 无法继承），
   显隐由外壳那层 div 的 v-show 负责。 */

interface Props {
  /** 外壳告知「当前就是本步」（= currentStep === 3）：用于挂载表格与进入时自动探测 */
  active: boolean;
  model: any;
  record: any;
  formType: string;
}
const props = defineProps<Props>();
const emit = defineEmits(['probeStart']);

const model = computed(() => props.model);

const userStore = useUserStore();
const current_org = userStore.currentOrg;

/* ═══ 列配置子表（§3.1.4） ═══
   ★ v29.23：数据来源两路——编辑态预取走 DB（loadColumns）；探测刷新走 probe_columns
   （基于第 1 步表单的 SQL+数据源实时探测，不读写库），按 data_index 合并：
   已有列保留全部人工配置（class_name/sql_type 属类型信息，随探测更新）、
   新列补默认行；探测里已消失的列保留在末尾（不清除现有配置，可手动删行）。 */
const colRows = ref<any[]>([]);
const colLoading = ref(false);
/* v29.11：本次保存要删除的 data_index（删除仅移除列配置行，不改 SQL） */
const deletedDataIndexes = ref<string[]>([]);

/* ═══ ★ v29.34（用户定案）：列配置表换 s-table + 容器自适应高度 ═══
   高度算法照抄 `tablePro/tableConfig/columnsConfig.vue` 的 `tableHeight`：
   **量「装表格的那层容器」，把它原样交给 s-table 当整体高度**。
   目标不变量（只有一条）：**整个本步（列配置）里只能有 s-table 自己的那根滚动条**——
   表体纵向滚动由 s-table 负责，横向由 `scroll.x` 负责，面板（`.step-pane.step-pane--col`）既不滚也不溢。
   推论：`colTableHeight` 必须等于「面板高 − 工具条高 − 间距」，多 1px 就会把面板撑出
   第二根滚动条，少 1px 表格底部就留白。所以链路必须整段闭合，断任何一环都拿不到这个差：
     ① `.step-pane.step-pane--col`（在外壳，作用在外壳那层 div 上）：定高（继承 .step-pane 的
        `calc(80vh - 150px)`）+ flex column + `overflow: hidden`（兜住亚像素误差）；
     ② `.col-table-wrapper`（本组件）：`flex: 1; min-height: 0`，**且只包表格、不包工具条**——
        它的高度由 flex 解析出来（不是靠内容撑），正好等于「剩余高度」；
        这也是能安全观测自己的原因：高度不依赖表格内容，不存在「观测→改高→再观测」循环；
     ③ `:height="colTableHeight"`：s-table 的整体高度（表头 + 表体，s-table 用 `height`
        表达 scroll.y）。留白方向偏安全：用整数 `clientHeight` 截断，宁可差不到 1px 也不外溢。
   实测来源两路互补：
     - `useResizeObserver`：容器几何变化（切步骤 / 弹窗尺寸变化 / 字体缩放）就重下发；
     - 首次进入本步时先挂载（见下方 watch(active)）、再 `nextTick` 显式量一次做兜底，
       且 `if (h > 0)` 守卫保证「隐藏期间不清掉上一次的有效高度」。 */
const colTableWrapRef = ref<HTMLElement | null>(null);
const colTableHeight = ref(320); // 兜底值：RO 首帧 / 隐藏期间报不出有效高度时的可用高度
const measureColTable = () => {
  const h = colTableWrapRef.value?.clientHeight || 0;
  if (h > 0) colTableHeight.value = h;
};
useResizeObserver(colTableWrapRef, entries => {
  const h = entries[0]?.contentRect?.height || 0;
  if (h > 0) colTableHeight.value = h;
});

/* s-table 的**挂载时机**：必须等本步真正完成布局之后再挂。
   为什么不能直接写 `v-if="active"`：`v-show` 的 `display` 是 Vue 的
   vShow 指令在 **updated 钩子**里写的，而指令钩子走 post-flush（整个 patch 结束之后）——
   于是「把表格插进 DOM」的那一刻，父级还是 display:none，s-table 首帧量到 0 宽 0 高。
   用 `nextTick` 把挂载推到 flush 之后即可（此时 display 已生效）。
   挂载后就不再卸载：来回切步骤由外壳的 v-show 隐藏，保留表格内部的横向滚动位置。 */
const colTableMounted = ref(false);
watch(
  () => props.active,
  act => {
    if (!act) return;
    colTableMounted.value = true;
    nextTick(measureColTable); // 挂载后立刻量一次；此后由 ResizeObserver 接管
    /* ★ v29.23：进入本步且当前无列（新建流程）→ 按基础配置的 SQL+数据源自动探测推断
       （原先写在外壳的 goNext / onStepChange 里，随本步一起下沉） */
    if (!colRows.value.length) runProbeRefresh();
  },
);

/* ★ v29.26（用户定案）：新建流程自动探测列后，若存在 `id` 列（不分大小写）则自动选它为主键。
   `pkAutoLocked` = 自动选择只做一次、且一旦用户手动动过主键就不再干预
   （否则用户取消勾选后再点「列探测刷新」又被自动勾回来）。 */
const pkAutoLocked = ref(false);

/* 在探测出的列集合里自动指定主键：命中 `id` 列且当前一列主键都没有时生效。
   ★ v29.40：主键可多选后本函数**只勾 id 那一列**（不再整列重置）—— 前置守卫已保证
   「当前一列主键都没有」，所以效果与原先的「互斥式整列重置」等价，但语义更直白。 */
const autoPickPrimaryKey = (rows: any[]) => {
  if (pkAutoLocked.value || props.formType === 'edit') return;
  if (rows.some((r: any) => !!r.is_primary_key)) return; // 已有主键（如探测保留的配置）不覆盖
  const idRow = rows.find(
    (r: any) =>
      String(r.data_index || '')
        .trim()
        .toLowerCase() === 'id',
  );
  if (!idRow) return;
  idRow.is_primary_key = true;
  pkAutoLocked.value = true;
};

/* ★ v29.24 引入 / ★ v29.40（用户定案）改为**可多选**：纯 toggle，不再「勾一个自动清其他」。
   语义与后端 rowKey 派生对齐（db_view/utils.resolve_row_key）：
     · 勾 0 列 → rowKey 落 '_id'，值 = 整行内容 hash（隐含主键）
     · 勾 1 列 → rowKey = 该列名
     · 勾 ≥2 列 → rowKey 落 '_id'，值 = 各主键列值用 '|' 连接
   后端保存侧原先的「最多一列 True」兜底已一并删除（is_primary_key 本来就逐行落库）。 */
const onPrimaryKeyChange = (record: any, e: any) => {
  pkAutoLocked.value = true; // ★ v29.26：用户手动改过 → 后续探测刷新不再自动指定
  record.is_primary_key = !!e?.target?.checked;
};

const buildDefaultColumn = (name: string, type: string, order: number) => ({
  data_index: name,
  title: name,
  query_title: undefined,
  column_order: order,
  width: 150,
  checked: true,
  ellipsis: true,
  resizable: true,
  summary: false,
  is_primary_key: false, // ★ v29.24：默认不指定主键（人工勾选；一列都不勾走隐含 _id）
  // ★ v29.23.2（用户定案）：新建时 可排序/基础搜索/默认搜索 默认全部勾选
  is_sortable: true,
  is_default_sort: false,
  default_sort_type: null,
  default_sort_order: null,
  class_name: PROBE_CLASS_NAME_MAP[type] || 'str',
  sql_type: type || '',
  cell_component: PROBE_CELL_MAP[type] || '',
  is_base_query: true,
  is_default_query: true,
  is_group_query: false,
});

const probing = ref(false);

const runProbeRefresh = async () => {
  if (!model.value.data_source_id || !model.value.sql) {
    message.warning('请先在「基础配置」步骤选择数据源并填写 SQL');
    return;
  }
  probing.value = true;
  /* ★ v29.35：原先是 `sqlError.value = ''`（清掉第 1 步可能残留的预览报错）——
     sqlError 归外壳持有（第 1 步渲染它），这里改成事件通知，行为不变 */
  emit('probeStart');
  try {
    const res: any = await getApiData(
      { org_id: current_org?.id, data_source_id: model.value.data_source_id, sql: model.value.sql },
      `${props.record.model_app}/probe_columns`,
    );
    const payload = extractData(res);

    const cols: any[] = payload?.columns || [];
    if (!cols.length) {
      message.warning('探测到 0 列，请检查 SQL 输出');
      return;
    }
    /* 按 data_index 合并：命中行保留配置只更新类型信息；新列追加默认行；已消失列保留 */
    const byIdx = new Map<string, any>(colRows.value.map((r: any) => [r.data_index, r]));
    const next: any[] = [];
    const seen = new Set<string>();
    cols.forEach((c: any, i: number) => {
      if (seen.has(c.name)) return; // SQL 输出重名列兜底（unique_together 会炸）
      seen.add(c.name);
      const exist = byIdx.get(c.name);
      if (exist) {
        exist.class_name = PROBE_CLASS_NAME_MAP[c.type] || exist.class_name || 'str';
        exist.sql_type = c.type || exist.sql_type || '';
        next.push(exist);
      } else {
        next.push(buildDefaultColumn(c.name, c.type || '', i + 1));
      }
    });
    colRows.value.forEach((r: any) => {
      if (!seen.has(r.data_index)) next.push(r); // 探测里没有的现有列：原样保留（不清除配置）
    });
    // ★ v29.26：新建流程探测后若存在 `id` 列，自动选为主键（用户手动改过则不干预）
    autoPickPrimaryKey(next);
    colRows.value = next;
  } finally {
    probing.value = false;
  }
};

/* ★ v29.18：默认排序开关——开启时默认升序 + 排序顺序=1（用户可再改） */
const onDefaultSortToggle = (record: any) => {
  if (record.is_default_sort) {
    record.default_sort_type = record.default_sort_type || 'asc';
    record.default_sort_order = record.default_sort_order || 1;
  }
};

/* 编辑态预取已保存的列配置（★ v29.23：仅作底数，列探测刷新可在此基础上实时合并） */
const loadColumns = async () => {
  if (props.formType !== 'edit' || props.record?.id == null) return;
  colLoading.value = true;
  try {
    /* ★ v29.8.1（用户定案）：读取走 lybase 通用 query_db_view_column_list，
       filter_fields 契约按 view_id 过滤；不传 sort_fields → 按 Meta.ordering（column_order）排序 */
    const res = await getApiData(
      {
        org_id: current_org?.id,
        filter_fields: ['view_id'],
        view_id: props.record.id,
        is_pagination: false,
      },
      `${props.record.model_app}/query_db_view_column_list`,
    );
    colRows.value = toList(res);
    deletedDataIndexes.value = []; // 刷新 = 放弃本地未保存的删改
  } catch (e) {
    colRows.value = [];
  } finally {
    colLoading.value = false;
  }
};

const removeColumn = (record: any) => {
  colRows.value = colRows.value.filter((r: any) => r.data_index !== record.data_index);
  deletedDataIndexes.value.push(record.data_index);
};

/* ═══ ★ v29.16：列配置行内两个模态框 ═══ */
/* 转换/颜色（content_map + color_map 联合配置，用户定案：不割裂为两个入口） */
const mapColorOpen = ref(false);
const mapColorRecord = ref<any>(null);
const openMapColorModal = (record: any) => {
  mapColorRecord.value = record;
  mapColorOpen.value = true;
};
const mapCount = (record: any) => {
  const keys = new Set([
    ...Object.keys(record?.content_map || {}),
    ...Object.keys(record?.color_map || {}),
  ]);
  return keys.size;
};
/* 高级搜索组件（完整组件配置对象，JSONField 落库） */
const advSearchOpen = ref(false);
const advSearchRecord = ref<any>(null);
const openAdvSearchModal = (record: any) => {
  advSearchRecord.value = record;
  advSearchOpen.value = true;
};

/* ═══ ★ v29.23：列配置随视图一起保存（save_view 同时落两表） ═══
   此前 doSaveColumns/saveColumns 独立按钮与连带保存已移除——外壳 handleOk 统一打包
   主表字段 + columns + deleted_data_indexes 提交 save_view（新建/编辑共用），
   故这里把两个「取值口」expose 给外壳。 */
const buildColumnsPayload = () =>
  colRows.value.map((r: any) => ({
    data_index: r.data_index,
    column_order: r.column_order,
    title: r.title,
    query_title: r.query_title,
    checked: r.checked,
    ellipsis: r.ellipsis,
    resizable: r.resizable,
    summary: r.summary,
    is_sortable: !!r.is_sortable, // ★ v29.16：sorter(JSON) → is_sortable(Bool)
    is_default_sort: !!r.is_default_sort, // ★ v29.18：默认排序三字段
    default_sort_type: r.is_default_sort ? r.default_sort_type || 'asc' : null,
    default_sort_order: r.is_default_sort ? r.default_sort_order ?? null : null,
    cell_component: r.cell_component,
    is_base_query: r.is_base_query,
    is_default_query: r.is_default_query,
    is_group_query: r.is_group_query,
    content_map: r.content_map ?? null, // ★ v29.16：转换/颜色 模态框产出
    color_map: r.color_map ?? null,
    advanced_search_component: r.advanced_search_component ?? null, // ★ v29.16：组件配置对象
    // ★ v29.23.1：类型信息随保存落库——后端 _upsert_columns 白名单已放行；
    //   漏传会导致编辑态探测出的类型保存即丢（保存后再次编辑类型恒空）
    class_name: r.class_name || '',
    sql_type: r.sql_type || '',
    // ★ v29.24：主键标记。★ v29.40：可多选 —— 逐行原样照传，后端不再做「最多一列」收敛
    is_primary_key: !!r.is_primary_key,
  }));

defineExpose({
  /** 提交用：列配置载荷（外壳 handleOk 取） */
  buildColumnsPayload,
  /** 提交用：本次要删除的 data_index（外壳 handleOk 取） */
  getDeletedIndexes: () => [...deletedDataIndexes.value],
  /** 保存成功后：清空待删除列表（原先由外壳直接 `deletedDataIndexes.value = []` 完成） */
  resetDeletedIndexes: () => {
    deletedDataIndexes.value = [];
  },
});

onMounted(() => {
  /* 编辑态预取列配置（原先在外壳 onMounted 的 edit 分支里调；本组件的 mounted 早于外壳，
     但 `formType` / `record.id` 都由外壳在 setup 顶层就绪，读到的值一定是对的） */
  if (props.formType === 'edit') loadColumns();
});
</script>

<style scoped lang="less">
.meta-tip {
  margin-top: 4px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  line-height: 1.5;
}
.col-toolbar {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
}
/* ★ v29.34：s-table 的高度来源容器——flex:1 撑满工具条之外的空间；min-height:0 让
   它能在内容更高时被压缩（否则 flex 子项默认 min-height:auto，会顶破父级定高）。 */
.col-table-wrapper {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}
/* ★ v29.18：默认排序单元格——开关 + 升降序 + 顺序 横排 */
.default-sort-cell {
  display: flex;
  align-items: center;
  gap: 4px;
}
</style>
